import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, FileText } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QuoteBadge } from "@/components/Badges";
import { api, inr, fmtDate } from "@/lib/api";

const TABS = [["all", "All"], ["draft", "Draft"], ["pending_approval", "Awaiting"], ["approved", "Approved"], ["rejected", "Changes"]];

export default function QuoteList({ base, admin = false }) {
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState(admin ? "pending_approval" : "all");

  useEffect(() => {
    api.get("/quotations", { params: tab === "all" ? {} : { status: tab } }).then((r) => setQuotes(r.data));
  }, [tab]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className={`font-heading font-extrabold text-slate-900 tracking-tight ${admin ? "text-4xl" : "text-3xl"}`}>Quotations</h1>
          <p className="text-sm text-slate-500 mt-1">Every quotation needs Director approval before the PDF can be shared.</p>
        </div>
        <Link to={`${base}/new`} data-testid="new-quotation-button"
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 transition-colors">
          <FilePlus2 className="h-4 w-4" />New Quotation
        </Link>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full overflow-x-auto justify-start">
          {TABS.map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`quotes-tab-${k}`}>{v}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className={admin ? "grid md:grid-cols-2 xl:grid-cols-3 gap-3" : "space-y-3"} data-testid="quotation-list">
        {quotes.length === 0 && <div className="text-sm text-slate-500 bg-white border border-dashed rounded-xl p-8 text-center col-span-full">No quotations here.</div>}
        {quotes.map((q, i) => (
          <Link key={q.id} to={`${base}/${q.id}`} data-testid={`quotation-card-${q.id}`} style={{ animationDelay: `${i * 50}ms` }}
            className="fade-up block bg-white rounded-xl border p-4 hover:border-sky-400 transition-[border-color]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono-num text-xs text-slate-500 flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{q.quote_no}</span>
              <QuoteBadge status={q.status} />
            </div>
            <div className="font-heading font-bold text-lg text-slate-900 mt-2 leading-snug">{q.customer.hospital_name}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{q.items.map((it) => it.name).join(", ")}</div>
            <div className="flex items-end justify-between mt-3">
              <div className="text-[11px] text-slate-500">{admin && `${q.created_by_name} · `}{fmtDate(q.created_at)}</div>
              <div className="font-mono-num font-bold text-slate-900">{inr(q.grand_total)}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
